import { useCallback, useEffect, useState } from 'react';

import LZString from 'lz-string';

const hasWindow = () => typeof window !== undefined;

const MAX_EVICTION_SIZE_WARNING: number = 2.5;
const MAX_EVICTION_SIZE: number = 3.0;

export const RECEIVE_STORAGE = 'receive-db';

const urlParams = new URLSearchParams(window.location.search);
const logCompressionParam = urlParams.get('logCompression');
const logCompression = logCompressionParam === 'true' || false;
const NON_COMPRESSED_LIST = ['show-in-progress', 'unifiedReceivePreferredSite', 'userId'];

// function to return the size of localstorage in MB
const localStorageSizeinMB = function () {
  let total = 0,
    length;
  for (let key in localStorage) {
    if (!localStorage.hasOwnProperty(key)) continue;
    length = (localStorage[key].length + key.length) * 2;
    total += length;
  }
  return (total / Math.pow(1024, 2)).toFixed(2);
};

export const showEvictWarning = () => {
  const localStorageSize = localStorageSizeinMB();
  const evict = parseFloat(localStorageSize) > MAX_EVICTION_SIZE_WARNING;
  if (evict) {
    console.warn(`#useLocalStorage: localstorage is filled and browser storage will be evicted`);
  }
  return evict;
};

export const shouldEvict = () => {
  const localStorageSize = localStorageSizeinMB();
  return parseFloat(localStorageSize) > MAX_EVICTION_SIZE;
};

/**
 *
 * @param key string
 * @param initialValue any
 * @returns {UseLocalStorageResult}
 */
function useLocalStorage(key: string, initialValue?: unknown) {
  const shouldStorageEvict = shouldEvict();

  if (!window?.LZString) {
    window.LZString = LZString;
  }

  const loadStoredValue = useCallback(() => {
    if (!hasWindow()) return initialValue;

    try {
      const item = window.localStorage.getItem(key);
      const decompress = NON_COMPRESSED_LIST.includes(key) ? item : LZString.decompress(item);
      if (logCompression && key === RECEIVE_STORAGE && decompress) {
        console.log('#logCompression', JSON.parse(decompress));
      }
      return decompress ? JSON.parse(decompress) : initialValue;
    } catch (error) {
      console.warn(`useLocalStorage: an error occurred loading the localStorage key “${key}”:`, error);
      return initialValue;
    }
  }, [initialValue, key]);

  const [storedValue, setStoredValue] = useState(loadStoredValue);

  useEffect(() => {
    if (shouldStorageEvict) {
      // only remove the RECEIVE_STORAGE
      window.localStorage.removeItem(RECEIVE_STORAGE);
    }
  }, [shouldStorageEvict]);

  const setValue = (value: unknown) => {
    try {
      const valueToStore = typeof value === 'function' ? value(storedValue) : value;
      const strinfiedvalue = JSON.stringify(valueToStore);
      var compressed = NON_COMPRESSED_LIST.includes(key) ? strinfiedvalue : LZString.compress(strinfiedvalue);
      window.localStorage.setItem(key, compressed);

      setStoredValue(valueToStore);
    } catch (error) {
      console.warn(`useLocalStorage: error setting the localStorage key “${key}”:`, error);
    }
  };

  useEffect(() => {
    setStoredValue(loadStoredValue());
  }, [loadStoredValue]);

  return [storedValue, setValue];
}

export default useLocalStorage;
